var _div;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { node, object, string } from 'prop-types';
import { styled } from '@mui/material/styles';
import { useWindowSize } from '../hooks';
import SovosTooltip from '../sovos-tooltip';
import { ResizeContext } from '../internals/utils';
const StyledSpan = styled('span')({
  display: 'inline-block',
  margin: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  padding: 0,
  textOverflow: 'ellipsis',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
});
export const shouldOverflowStateChange = (scrollWidth, width, prevState) => {
  if (scrollWidth > width && !prevState) {
    return true;
  }
  if (scrollWidth <= width && prevState) {
    return true;
  }
  return false;
};
const getToolTipContent = (separator, text, textDoesOverflow, toolTipText) => {
  if (textDoesOverflow && toolTipText) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, text, " ", separator, " ", toolTipText);
  }
  if (textDoesOverflow) {
    return text;
  }
  if (toolTipText) {
    return toolTipText;
  }
  return undefined;
};

/**
 * Fixed Width Text shows ellipsis & tooltip on non-wrapping text that
 * overflows it's container.
 */
const SovosFixedWidthText = _ref => {
  let {
    className,
    separator = '•',
    style = {},
    text,
    toolTipText,
    ...rest
  } = _ref;
  const [textDoesOverflow, setTextDoesOverflow] = useState(false);
  const textRef = useRef(null);
  const {
    width: windowWidth
  } = useWindowSize();
  const {
    width: contextWidth
  } = useContext(ResizeContext);
  useEffect(() => {
    if (text == null) return;
    const textScrollWidth = textRef.current.scrollWidth;
    const textWidth = textRef.current.offsetWidth;
    if (shouldOverflowStateChange(textScrollWidth, textWidth, textDoesOverflow)) {
      setTextDoesOverflow(prevState => !prevState);
    }
  }, [textDoesOverflow, text, windowWidth, contextWidth]);
  if (text == null) return null;
  const toolTipContent = getToolTipContent(separator, text, textDoesOverflow, toolTipText);

  // The content element needs to have an empty, block display, child element to prevent Safari from showing
  // a native tooltip for the overflowing text.
  let content = /*#__PURE__*/React.createElement(StyledSpan, _extends({
    className: clsx('sovosFixedWidthText', className),
    style: style,
    ref: textRef,
    key: text
  }, rest), _div || (_div = /*#__PURE__*/React.createElement("div", null)), text);
  if (toolTipContent) {
    content = /*#__PURE__*/React.createElement(SovosTooltip, {
      title: toolTipContent
    }, content);
  }
  return content;
};
SovosFixedWidthText.propTypes = {
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * A custom separator between the `text` and `toolTipText` in the tooltip
   */
  separator: node,
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * The text to be displayed and shown in a tooltip when truncated
   */
  text: node,
  /**
   * Content to be displayed only in the tooltip, regardless of whether
   * `text` is truncated. When present, a tooltip will always be available
   */
  toolTipText: string
};
export default SovosFixedWidthText;